import request from 'api/requestUtils'
import config from 'constants/config'
import { ALL_GRAPH_RECOMMENDATION_OFFER_TYPES, ALL_GRAPH_RECOMMENDATION_OFFER_TYPES_NO_LPP, ALL_GRAPH_RECOMMENDATION_OFFER_TYPES_NO_LPP_OR_MARRIOTT } from 'constants/recommendations'
import { isLereEnabled, isLereHighIntentEnabled, isLereMetaSearchEnabled } from 'lib/lere/isLereEnabled'
import qs from 'qs'
import type { Recommendation } from './mappers/recommendationMap'
import { offerRecommendationMap } from './mappers/recommendationMap'

function getLereEnvParameters(): Record<string, string> {
  return config.LERE_USE_MOCKS ? { use_mocks: 'true' } : {}
}

export const GENERIC_LERE_API_ABORT_AFTER_MS = 8000
const HOMEPAGE_LERE_API_ABORT_AFTER_MS = 2000

const TWO_TOWERS_INTERACTION_CUTOFF_DAYS = 30
const BASE_HIGH_INTENT_URL = '/api/recommendation/high_intent/'
interface RecommendationURLParams {
  check_in?: string
  check_out?: string
  available_month?: string
  duration?: number
  region?: string
  allow_multi_offers_same_property?: boolean
  flex?: number
  minMpv?: number
}

class RecommendationsURLBuilder {
  // @ts-ignore
  private baseUrl: string
  private offerId: string
  private queryParams: Record<string, string | number | boolean> = {}

  constructor(offerId: string) {
    this.offerId = offerId
  }

  withCalendarBaseUrl() {
    this.baseUrl = `/api/recommendation/offers/${this.offerId}/calendar`
    return this
  }

  withBaseUrl() {
    this.baseUrl = `/api/recommendation/offers/${this.offerId}`
    return this
  }

  withQueryParams(queryParams: RecommendationURLParams) {
    this.queryParams = { ...this.queryParams, ...queryParams }
    return this
  }

  withLereEnvParameters() {
    this.queryParams = { ...this.queryParams, ...getLereEnvParameters() }
    return this
  }

  withRoomParams(rooms?: Array<App.Occupants>) {
    if (rooms?.length) {
      const occupancy = occupancyToStr(rooms)
      this.queryParams.occupancy = occupancy
    }
    return this
  }

  build() {
    return `${this.baseUrl}?${qs.stringify(this.queryParams)}`
  }
}

export function occupancyToStr(rooms: Array<App.Occupants>) {
  const occupancy = rooms.map(room => {
    let roomParam = `${room.adults}`
    if (room?.childrenAge?.length) {
      roomParam += `-${room.childrenAge.join(',')}`
    }
    return roomParam
  }).join(';')

  return occupancy
}

export function monthIndexToMonth(monthIndex: string) {
  const [monthStr, yearStr] = monthIndex.split('-')
  const month = parseInt(monthStr)
  const year = parseInt(yearStr)

  return `${month + 1}-${year}`
}

export interface Filters {
  checkIn?: string
  availableMonth?: string
  rooms?: Array<App.Occupants>
  duration?: number
  offerTypes?: Array<string>
}

interface LereResult {
  offers: Array<App.Recommendation>
  lereVersion: string | undefined
}

export async function getRecommendations(offerId: string, region: string, isLuxPlusMember: boolean, allowMultiOffersSameProperty: boolean = false, checkIn?: string, checkOut?: string, occupancy?: string, minMpv?: number, allowWhiteLabels: boolean = false): Promise<LereResult> {
  if (!isLereEnabled() && !(allowWhiteLabels && (isLereHighIntentEnabled() || isLereMetaSearchEnabled()))) {
    return { offers: [], lereVersion: undefined }
  }

  const queryParams: {
    region: string
    is_lux_plus_member: boolean
    allow_multi_offers_same_property: boolean
    occupancy: string | undefined
    check_in: string | undefined
    check_out: string | undefined
    offer_types: string
    min_mpv?: number
  } = {
    region,
    is_lux_plus_member: isLuxPlusMember,
    allow_multi_offers_same_property: allowMultiOffersSameProperty,
    occupancy: (occupancy && checkIn && checkOut) ? occupancy : undefined,
    check_in: (occupancy && checkIn && checkOut) ? checkIn : undefined,
    check_out: (occupancy && checkIn && checkOut) ? checkOut : undefined,
    offer_types: ALL_GRAPH_RECOMMENDATION_OFFER_TYPES.join(','),
    min_mpv: minMpv,
  }

  const builder = new RecommendationsURLBuilder(offerId)
    .withLereEnvParameters()
    .withQueryParams(queryParams)
  const url = builder.withBaseUrl().build()
  const response = await request.get<App.ApiResponse<Array<Recommendation>>>(url, { credentials: 'include', timeout: GENERIC_LERE_API_ABORT_AFTER_MS })
  const { lere_version } = response.meta || {}

  const offers = response.result.map(recommendation => offerRecommendationMap(recommendation))
  return { offers, lereVersion: lere_version }
}

export function getChildrenAgesValid(rooms?: Array<App.Occupants>) {
  return (rooms || []).every((room) => {
    return (room.childrenAge || []).every((age) => age >= 0)
  })
}

export async function getCalendarRecommendations(
  offerId: string,
  region: string,
  isLuxPlusMember: boolean,
  filters: Filters,
): Promise<LereResult> {
  if (!isLereEnabled()) {
    return { offers: [], lereVersion: undefined }
  }

  const { checkIn, rooms, availableMonth, duration } = filters

  if (!getChildrenAgesValid(rooms)) {
    throw new Error('Some children ages are < 0')
  }

  const useCalendarAPI = !checkIn

  const queryParams = {
    duration,
    region,
    is_lux_plus_member: isLuxPlusMember,
    allow_multi_offers_same_property: true,
    offer_types: ALL_GRAPH_RECOMMENDATION_OFFER_TYPES.join(','),
  }
  const builder = new RecommendationsURLBuilder(offerId)
    .withLereEnvParameters()
    .withQueryParams(queryParams)
    .withRoomParams(rooms)

  if (useCalendarAPI) {
    // call calendar reco API for flexible month
    builder.withCalendarBaseUrl().withQueryParams({ available_month: availableMonth })
  } else {
    // call offer seeded reco API for exact date
    builder.withBaseUrl().withQueryParams({ check_in: checkIn })
  }

  const url = builder.build()
  const response = await request.get<App.ApiResponse<Array<Recommendation>>>(url, { credentials: 'include', timeout: GENERIC_LERE_API_ABORT_AFTER_MS })
  const { lere_version } = response.meta || {}
  const offers = response.result.map(recommendation => offerRecommendationMap(recommendation))
  return { offers, lereVersion: lere_version }
}

export async function removeAbandonedCarts(offerIds: Array<string>, domainUserId?: string, userId?: string) {
  if (!offerIds.length || (!domainUserId && !userId)) {
    return
  }

  if (!isLereEnabled()) {
    return
  }

  let url = `${BASE_HIGH_INTENT_URL}abandoned_carts/remove?without_brand=1`
  if (userId) {
    url += `&le_user_id=${userId}`
  }
  if (domainUserId) {
    url += `&domain_user_id=${domainUserId}`
  }
  return await request.post(url, offerIds, { credentials: 'include', timeout: GENERIC_LERE_API_ABORT_AFTER_MS }).catch(console.error)
}

export async function getTopDeals(region: string, isLuxPlusMember: boolean = false) {
  if (!isLereEnabled()) {
    return { offers: [], lereVersion: undefined }
  }

  const url = ['/api/recommendation/top_deals', qs.stringify({ region, is_lux_plus_member: isLuxPlusMember, ...getLereEnvParameters() })].join('?')
  const response = await request.get<App.ApiResponse<Array<LereRecommendation>>>(url, { credentials: 'include', timeout: GENERIC_LERE_API_ABORT_AFTER_MS })

  const { lere_version } = response.meta || {}
  return { offers: response.result, lereVersion: lere_version }
}

export async function getHeroPlanner(region: string) {
  if (!isLereEnabled()) {
    return { offerIds: [], lereVersion: undefined }
  }

  const url = ['/api/recommendation/hero_planner', qs.stringify({ region })].join('?')
  const response = await request.get<App.ApiResponse<Array<string>>>(url, { credentials: 'include', timeout: GENERIC_LERE_API_ABORT_AFTER_MS })

  const { lere_version } = response.meta || {}
  return { offerIds: response.result, lereVersion: lere_version }
}

interface LereHighIntent {
  offer_id: App.HighIntentOffer['offerId']
  offer_type: App.HighIntentOffer['offerType']
  creation_time: App.HighIntentOffer['creationTime']
  lere_version?: App.HighIntentOffer['lereVersion']
  is_price_missing?: App.HighIntentOffer['isPriceMissing']
  category: App.HighIntentOffer['category']
  occupancy?: App.HighIntentOffer['occupancy']
  isDismissed?: App.HighIntentOffer['isDismissed']
}

export async function removeHotLeads(offerIds: Array<string>, domainUserId?: string, userId?: string) {
  if (!offerIds.length || (!domainUserId && !userId)) { return }

  if (!isLereEnabled()) {
    return
  }

  let url = `${BASE_HIGH_INTENT_URL}hot_leads/remove?without_brand=1`
  if (userId) {
    url += `&le_user_id=${userId}`
  }
  if (domainUserId) {
    url += `&domain_user_id=${domainUserId}`
  }
  await request.post(url, offerIds, { credentials: 'include', timeout: GENERIC_LERE_API_ABORT_AFTER_MS }).catch(console.error)
}

export async function removeRecentlyViewedOffers(offerIds: Array<string>, domainUserId?: string, userId?: string) {
  if (!offerIds.length || (!domainUserId && !userId)) { return }

  if (!isLereEnabled()) {
    return
  }

  let url = `${BASE_HIGH_INTENT_URL}recently_viewed/remove?without_brand=1`
  if (userId) {
    url += `&le_user_id=${userId}`
  }
  if (domainUserId) {
    url += `&domain_user_id=${domainUserId}`
  }
  // we need to post an array and have to disable the brand pre-processor
  // else it will turn the request body into an object
  await request.post(url, offerIds, { credentials: 'include', timeout: GENERIC_LERE_API_ABORT_AFTER_MS }).catch(console.error)
}

const mapLereHighIntent = (lereOffer: LereHighIntent): App.HighIntentOffer => ({
  ...lereOffer,
  creationTime: lereOffer.creation_time ?? 0,
  category: lereOffer.category,
  isDismissed: !!lereOffer.isDismissed,
  occupancy: lereOffer.occupancy,
  isPriceMissing: !!lereOffer.is_price_missing,
  offerId: lereOffer.offer_id,
  offerType: lereOffer.offer_type,
})

function mapUserInteractionRecentlyViewed(interaction: App.OfferInteraction): App.RecentlyViewedOffer {
  return {
    creationTime: interaction.interaction_time,
    category: 'recently_viewed',
    offerId: interaction.offer_id,
    offerType: interaction.offer_type ?? 'hotel',
  }
}

interface LereRecommendation {
  offer_id: App.RecommendationOffer['offerId']
  offer_type: App.RecommendationOffer['offerType']
  score?: App.RecommendationOffer['score']
}

function mapLereRecommendation(lereOffer: LereRecommendation): App.RecommendationOffer {
  const { offer_id, offer_type, score } = lereOffer

  return {
    offerId: offer_id,
    offerType: offer_type,
    score,
  }
}

namespace Enquiry {
  type PersonalisationCategories = 'people_like_me' | 'alternatives' | 'preference' | 'sticky_abandoned_carts' | 'hot_leads' | 'sticky_hot_leads' | 'recently_viewed' | 'two_towers'
  type PersonalisationBody = {
    check_in?: string
    check_out?: string
    occupancy?: string
    model_version?: string
    offer_id?: string
  }

  export type Body<K extends PersonalisationCategories> = {
    [P in K]: PersonalisationBody
  }
  export type CategorisedResponse<K extends PersonalisationCategories, R = LereRecommendation | LereHighIntent> = {
    [P in K]: {
      result: Array<R>
      meta?: {
        lere_version: string
      }
    }
  }
  export type AggregatedResponse = Array<LereRecommendation | LereHighIntent>
}

export async function getHighIntentOffers(region: string, leUserId?: string, domainUserId?: string): Promise<{ offers: Array<App.HighIntentOffer>, lereVersion: string }> {
  const query = {
    region,
    limit: 15,
    // the order matters, front ones returned first, later ones get deduped
    categories: 'sticky_abandoned_carts,sticky_hot_leads,recently_viewed',
    aggregation_mode: 'combine',
    domain_user_id: domainUserId,
    le_user_id: leUserId,
  }
  const body = { sticky_abandoned_carts: {}, sticky_hot_leads: {}, recently_viewed: {} }
  const url = `/api/recommendation/enquiry?${qs.stringify(query)}`

  const response = await request.post<App.ApiResponse<Enquiry.AggregatedResponse>, Enquiry.Body<'sticky_abandoned_carts' | 'sticky_hot_leads' | 'recently_viewed'>>(url, body, { timeout: HOMEPAGE_LERE_API_ABORT_AFTER_MS })

  const lereVersion = response.meta.lere_version
  const offers = response.result.map(mapLereHighIntent)

  return { offers, lereVersion }
}

export async function getRecentlyViewedOffers(leUserId: string): Promise<{ offers: Array<App.RecentlyViewedOffer>, lereVersion: string }> {
  if (!isLereEnabled()) {
    return { offers: [], lereVersion: '' }
  }

  const query = {
    le_user_id: leUserId,
    limit: 50,
    ...getLereEnvParameters(),
  }
  const url = `/api/recommendation/user-interactions/view?${qs.stringify(query)}`

  const response = await request.get<App.ApiResponse<undefined> & { data: Array<App.OfferInteraction> }>(
    url,
    { credentials: 'include', timeout: GENERIC_LERE_API_ABORT_AFTER_MS },
  )

  const offers = response.data.map(mapUserInteractionRecentlyViewed)

  return { offers, lereVersion: '' }
}

export async function getPeopleLikeMeRecommendations(leUserId: string, region: string, isLuxPlusMember: boolean = false): Promise<{ offers: Array<App.RecommendationOffer>, lereVersion: string }> {
  const category = 'people_like_me'
  const query = {
    categories: category,
    le_user_id: leUserId,
    region,
    is_lux_plus_member: isLuxPlusMember,
    offer_types: ALL_GRAPH_RECOMMENDATION_OFFER_TYPES_NO_LPP.join(','),
    ...getLereEnvParameters(),
  }
  const body = { [category]: { } }
  const url = `/api/recommendation/enquiry?${qs.stringify(query)}`

  const response = await request.post<App.ApiResponse<Enquiry.CategorisedResponse<'people_like_me'>>, Enquiry.Body<'people_like_me'>>(url, body, { timeout: HOMEPAGE_LERE_API_ABORT_AFTER_MS })

  const lereVersion = response.result[category].meta?.lere_version ?? response.meta.lere_version
  const offers = response.result[category].result.map(mapLereRecommendation)

  return { offers, lereVersion }
}

export async function getAlternativesRecommendations(region: string, isLuxPlusMember: boolean, leUserId?: string, domainUserId?: string): Promise<{ offers: Array<App.RecommendationOffer>, lereVersion: string }> {
  const category = 'alternatives'
  const query = {
    categories: category,
    le_user_id: leUserId,
    domain_user_id: domainUserId,
    region,
    is_lux_plus_member: isLuxPlusMember,
    limit: 18,
    offer_types: ALL_GRAPH_RECOMMENDATION_OFFER_TYPES_NO_LPP_OR_MARRIOTT.join(','),
    ...getLereEnvParameters(),
  }
  const body = { [category]: { } }
  const url = `/api/recommendation/enquiry?${qs.stringify(query)}`

  const response = await request.post<App.ApiResponse<Enquiry.CategorisedResponse<'alternatives'>>, Enquiry.Body<'alternatives'>>(url, body, { timeout: HOMEPAGE_LERE_API_ABORT_AFTER_MS })

  const lereVersion = response.result[category].meta?.lere_version ?? response.meta.lere_version
  const offers = response.result[category].result.map(mapLereRecommendation)

  return { offers, lereVersion }
}

export async function getPreferenceRecommendations(region: string, leUserId: string): Promise<{ offers: Array<App.RecommendationOffer>, lereVersion: string }> {
  const category = 'preference'
  const query = {
    categories: category,
    le_user_id: leUserId,
    region,
    ...getLereEnvParameters(),
  }

  const body = { [category]: {} }
  const url = `/api/recommendation/enquiry?${qs.stringify(query)}`

  const response = await request.post<App.ApiResponse<Enquiry.CategorisedResponse<'preference'>>, Enquiry.Body<'preference'>>(url, body, { timeout: HOMEPAGE_LERE_API_ABORT_AFTER_MS })

  const lereVersion = response.result[category].meta?.lere_version ?? response.meta.lere_version
  const offers = response.result[category].result.map(mapLereRecommendation)

  return { offers, lereVersion }
}

type Params = {
  region: string
  leUserId?: string
  domainUserId?: string
  isLuxPlusMember: boolean
  checkIn?: string
  checkOut?: string
  occupancy?: string
  offerId?: string
  limit?: number
  offerTypes?: Array<string>
  rankerEnabled?: boolean
  useClusters?: boolean
}

export async function getTwoTowersRecommendations({
  offerId,
  region,
  limit = 20,
  leUserId,
  domainUserId,
  isLuxPlusMember,
  checkIn,
  checkOut,
  occupancy,
  offerTypes,
  rankerEnabled,
  useClusters,
}: Params) {
  const category = 'two_towers'
  const request_mode = offerId ? 'offer_to_offer' : 'user_to_offer'
  const useLatestModelVersion = request_mode === 'user_to_offer'

  const query = {
    categories: category,
    region,
    limit,
    le_user_id: leUserId,
    domain_user_id: domainUserId,
    is_lux_plus_member: isLuxPlusMember,
    check_in: checkIn,
    check_out: checkOut,
    occupancy,
    offer_types: offerTypes?.join(','),
    ...(rankerEnabled !== undefined ? { use_ranker: rankerEnabled } : {}),
    ...(useLatestModelVersion ? { model_version: '0.4.20' } : {}),
    ...(useClusters !== undefined ? { use_clusters: useClusters } : {}),
    ...(request_mode === 'user_to_offer' ? { two_towers_interaction_cutoff_days: TWO_TOWERS_INTERACTION_CUTOFF_DAYS } : {}),
    request_mode,
    ...getLereEnvParameters(),
  }

  const body = { [category]: { offer_id: offerId } }
  const url = `/api/recommendation/enquiry?${qs.stringify(query)}`

  const timeout = offerId ? GENERIC_LERE_API_ABORT_AFTER_MS : HOMEPAGE_LERE_API_ABORT_AFTER_MS

  const response = await request.post<App.ApiResponse<Enquiry.CategorisedResponse<'two_towers'>>, Enquiry.Body<'two_towers'>>(url, body, { timeout })

  const lereVersion = response.result[category].meta?.lere_version ?? response.meta.lere_version
  const offers = response.result[category].result.map(mapLereRecommendation)

  return { offers, lereVersion }
}

interface LereOfferStats {
  offer_value: number | null
}

export async function getOfferStats(offerId: string, region: string) {
  if (!isLereEnabled()) {
    return { offerStats: { offerValue: null } }
  }

  const response = await request.get<App.ApiResponse<LereOfferStats>>(`/api/recommendation/offers/${offerId}/stats?region=${region}`, { credentials: 'include', timeout: GENERIC_LERE_API_ABORT_AFTER_MS })

  const lereStats = response.result
  const offerStats = {
    offerValue: lereStats.offer_value,
  }

  return { offerStats }
}

const TOUR_FETCH_LIMIT = 20

export async function getRecommendedTours(
  offerId: string,
  region: string,
  departureYear?: number,
  departureMonth?: number,
  offerTypes?: Array<App.Tours.V2ProductType>,
) {
  if (!isLereEnabled()) {
    return { offers: [], lereVersion: undefined }
  }

  const query = {
    ...getLereEnvParameters(),
    limit: TOUR_FETCH_LIMIT,
    region,
    ...(!!departureYear && departureMonth !== undefined) ? {
      departure_year: departureYear,
      departure_month: departureMonth + 1, // LERE API use 1-12 for month
    } : {},
    ...(offerTypes) ? {
      offer_types: offerTypes.join(','),
    } : {},
  }
  const url = [`/api/recommendation/tours/${offerId}`, qs.stringify(query)].join('?')
  const response = await request.get<App.ApiResponse<Array<LereRecommendation>>>(url, { credentials: 'include', timeout: GENERIC_LERE_API_ABORT_AFTER_MS })

  const { lere_version: lereVersion } = response.meta || {}

  const offers = response.result.map(mapLereRecommendation)

  return { offers, lereVersion }
}

export async function getRegionRecommendations(region: string): Promise<{ offers: Array<App.RecommendationOffer>, lereVersion: string | undefined }> {
  if (!isLereEnabled()) {
    return { offers: [], lereVersion: undefined }
  }

  const query = {
    categories: 'region_reco',
    region,
    ...getLereEnvParameters(),
  }
  const url = `/api/recommendation/enquiry?${qs.stringify(query)}`
  const response = await request.post<App.ApiResponse<{ region_reco: { result: Array<LereRecommendation> } }>, { region_reco: {} }>(
    url,
    { region_reco: {} },
    { credentials: 'include', timeout: GENERIC_LERE_API_ABORT_AFTER_MS })

  const lereVersion: string = response.meta.lere_version
  const offers: Array<App.RecommendationOffer> = response.result.region_reco.result.map(mapLereRecommendation)

  return { offers, lereVersion }
}

export async function getUrgentOfferRoomDates(offerId: string, roomTypeId: string, checkIn: string, checkOut: string) {
  if (!isLereEnabled()) {
    return {
      roomsRemaining: undefined,
      booked: undefined,
      allocated: undefined,
      travelDate: undefined,
    }
  }

  const query = {
    ...getLereEnvParameters(),
    offer_id: offerId,
    room_type_id: roomTypeId,
    start_date: checkIn,
    end_date: checkOut,
  }

  const url = `/api/recommendation/dynamic_pricing/urgent_room_dates?${qs.stringify(query)}`

  const response = await request.get<any>(url, { credentials: 'include', timeout: GENERIC_LERE_API_ABORT_AFTER_MS })
  return {
    roomsRemaining: response.rooms_left_before_surcharge,
    booked: response.booked,
    allocated: response.allocated,
    travelDate: response.travel_date,
  }
}

export async function getUserInteractions(
  interactionType: App.UserInteractionType,
  leUserId?: string,
  domainUserId?: string,
): Promise<{
  interactions: Array<App.OfferInteraction | App.SearchInteraction>
}> {
  const query = {
    le_user_id: leUserId,
    domain_user_id: domainUserId,
    limit: 50,
    ...getLereEnvParameters(),
  }
  const url = `/api/recommendation/user-interactions/${interactionType}?${qs.stringify(query)}`
  const response = await request.get<App.ApiResponse<undefined>>(url, { timeout: HOMEPAGE_LERE_API_ABORT_AFTER_MS })

  return { interactions: response.data }
}
